import React from 'react';
import styles from '../WarrantyRequest.module.scss';
import clsx from 'clsx';
import Chevron from 'assets/icons/chevron.svg?react';
import { AddPartModal } from '../WarrantyRequestPage.tsx';
import { IconButton } from '../../../../components/uikit/IconButton.tsx';
import PlusIcon from 'assets/icons/plus.svg?react';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import EditIcon from '../../../../assets/icons/edit.svg?react';
import CancelIcon from '../../../../assets/icons/close-button-small.svg?react';
import BinImage from 'assets/icons/bin.svg?react';
import { useAnalytics } from 'helpers/analytics.ts';
import {
  BasketPart,
  BasketRecord,
} from '../../../../services/api/api-client.types.ts';

export const WarrantyRequestPageCategory = 'Warranty request page';

type OwnProps = {
  records: BasketRecord[];
  closedOrders: Set<string>;
  handleClickAccordion: (key: string) => void;
  openAllAccordions: () => void;
  closeAllAccordions: () => void;
  setAddToCartModalInfo: (info: AddPartModal) => void;
  countOfParts: number;
  showRemoveItemFromCartModal: (itemNumber: string) => void;
  showEditPartModal: (
    cellInfo: BasketPart,
    productId: string,
    deviceName: string,
  ) => void;
  showClearCartModal: () => void;
};

export const Accordion = (props: OwnProps) => {
  const {
    records,
    closedOrders,
    handleClickAccordion,
    openAllAccordions,
    closeAllAccordions,
    setAddToCartModalInfo,
    countOfParts,
    showEditPartModal,
    showRemoveItemFromCartModal,
    showClearCartModal,
  } = props;

  const i18n = useScopedTranslation('Page.WarrantyPage');
  const { trackEvent } = useAnalytics();

  const outerTableTextStyles = [
    styles.text,
    styles.overflowEllipsis,
    styles.cursorDefault,
  ];

  const isAllAccordionClosed = closedOrders.size === records.length;

  return (
    <div className={styles.marginBottom65}>
      <div
        className={clsx(
          styles.expandAll,
          styles.rowContainer0,
          !!records.length ? styles.spaceBetween : styles.flexEnd,
          styles.borderBottomTotal,
        )}
      >
        {!!records.length && (
          <Chevron
            data-test-id={'expand-all'}
            className={clsx(
              styles.pointer,
              isAllAccordionClosed
                ? styles.rotate180
                : styles.marginBottomMinus2,
            )}
            onClick={() => {
              if (isAllAccordionClosed) {
                openAllAccordions();
              } else {
                closeAllAccordions();
              }
            }}
          />
        )}
        <div className={clsx(styles.rowContainer0, styles.marginRight22)}>
          <div className={styles.total}>{i18n.t('table_total')}:</div>
          <div
            data-test-id={'common-counter'}
            className={clsx(
              outerTableTextStyles,
              styles.marginLeft32,
              styles.marginRight237,
              !countOfParts && styles.marginRight254,
            )}
          >
            {countOfParts}
          </div>
          {!!countOfParts && (
            <IconButton
              testId={'clear-cart-button'}
              type={'destroy'}
              className={styles.marginBottom3}
              onClick={showClearCartModal}
            >
              <BinImage />
            </IconButton>
          )}
        </div>
      </div>
      {records.map((record, index) => (
        <div key={record.serialNumber}>
          <div
            className={clsx(
              styles.rowContainer0,
              styles.accordion,
              closedOrders.has(record.serialNumber) &&
                index !== records.length - 1 &&
                styles.borderBottom,
            )}
          >
            <div className={clsx(styles.rowContainer0)}>
              <div>
                <Chevron
                  data-test-id={'expand-one'}
                  className={clsx(
                    styles.pointer,
                    closedOrders.has(record.serialNumber)
                      ? styles.rotate180
                      : styles.marginBottomMinus2,
                  )}
                  onClick={() => handleClickAccordion(record.serialNumber)}
                />
              </div>
              <div className={clsx(outerTableTextStyles, styles.serialNumber)}>
                {record.serialNumber}
              </div>
              <div
                data-test-id={'group-name'}
                className={clsx(outerTableTextStyles)}
              >
                {record.name}
              </div>
            </div>
            <div className={clsx(styles.rowContainer0)}>
              <div
                data-test-id={'group-counter'}
                className={clsx(outerTableTextStyles)}
              >
                {record.parts.length}
              </div>
              <div className={styles.iconContainer}>
                <IconButton
                  testId={'group-add'}
                  type={'primary'}
                  iconClassName={clsx(styles.marginRight7)}
                  onClick={() => {
                    trackEvent({
                      category: WarrantyRequestPageCategory,
                      action: 'Open add last added part modal',
                      name: `Serial number: ${record.serialNumber}; serviceProductId: ${record.productId}`,
                    });

                    setAddToCartModalInfo?.({
                      visible: true,
                      sameDeviceInfo: {
                        serialNumber: record.serialNumber,
                        serviceProductId: record.productId,
                      },
                    });
                  }}
                >
                  <PlusIcon />
                </IconButton>
              </div>
            </div>
          </div>
          {!closedOrders.has(record.serialNumber) && (
            <div>
              {record.parts.map((part) => (
                <div
                  key={record.serialNumber + ',' + part.itemId}
                  className={clsx(
                    styles.rowContainer0,
                    styles.text,
                    styles.paddingTopBottom16,
                    styles.rowAccordion,
                    styles.spaceBetween,
                  )}
                >
                  <div className={clsx(styles.rowContainer0)}>
                    <div data-test-id={'item-name'} className={styles.width500}>
                      {part.name}
                    </div>
                    <div
                      data-test-id={'item-article'}
                      className={clsx(styles.width450)}
                    >
                      {part.article}
                    </div>
                  </div>
                  <div
                    className={clsx(
                      styles.rowContainer24,
                      styles.marginRight23,
                    )}
                  >
                    <IconButton
                      testId={'item-edit'}
                      onClick={() => {
                        trackEvent({
                          category: WarrantyRequestPageCategory,
                          action: 'Open edit part modal',
                          name: `Pos: ${part.itemId}; serial number: ${record.serialNumber};  part article: ${part.article}`,
                        });
                        showEditPartModal?.(
                          part,
                          record.productId,
                          record.name!,
                        );
                      }}
                      type={'primary'}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      testId={'item-remove'}
                      onClick={() => {
                        trackEvent({
                          category: WarrantyRequestPageCategory,
                          action: 'Remove item from cart',
                          name: `Pos: ${part.itemId}; serial number: ${record.serialNumber};  part article: ${part.article}`,
                        });
                        showRemoveItemFromCartModal(part.itemId);
                      }}
                      type={'destroy'}
                    >
                      <CancelIcon />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
